import Collection from "../../../data-structures/collection"
import UserToAdd from "./add-user"
import CompanyUser from "../../../model2/company-user-new/company-user"
import Workspace from "../../../model2/space/workspaces/workspace"
import {ProjectAccess} from "../../../gd/const/project"
import {AddUser_ALL, AddUser_Manager} from "./manager"
import ProjectSession from "../../../model2/project/project-session"
import Task from "../../../model2/task/task"

export default class UsersToAdd extends Collection<UserToAdd> {

    protected manager               : AddUser_Manager;
    private defaultWorkspaceAccess  : ProjectAccess = ProjectAccess.STANDARD
    private defaultProjectAccess    : ProjectAccess = ProjectAccess.STANDARD

    constructor(manager: AddUser_Manager, models: UserToAdd[] = []) {
        super(models)

        this.manager = manager
    }

    get atLeastOneChecked(): boolean {
        const firstChecked = _.find(this.models, (userToAdd: UserToAdd) => {
            return userToAdd.isSelected
        })

        return !!firstChecked;
    }

    get selected(): UserToAdd[] {
        return _.filter(this.models, (userToAdd)=>{
            return userToAdd.isSelected
        })
    }

    unselectAll() {
        _.map(this.models, (userToAdd: UserToAdd) => {
            return userToAdd.unselect()
        })
    }

    resetForTask(task:Task):void {
        const models : UserToAdd[] = []

        // 1. find all task user ids
        const existingUserIds : ID[] = task.userIds

        // 2. find project users who have access at least LIMITED and are not in this task yet
        const notInProject : ID[] = _.filter(task.project!.userIds(ProjectAccess.LIMITED), (userId:ID)=>{
            return !existingUserIds.includes(userId)
        })

        _.map(notInProject, (userId:ID)=>{
            const companyUser = gd.session.companyUsers.getByUserId(userId)
            if (companyUser) {
                const userToAdd = new UserToAdd(companyUser)
                models.push(userToAdd)
            }
        })

        this.reset(models)
    }

    resetForWorkspace(workspace:Workspace, task?: Task):void {
        const models : UserToAdd[] = []

        // 1. find all company users who are not part of this workspace yet
        let existingUserIds : ID[] = workspace.project.userIds()
        if (task) existingUserIds = _.union(existingUserIds, task.userIds)

        // 2. find company users who are not customers and not in this workspace yet
        const notInWorkspace : CompanyUser[] = _.filter(gd.session.companyUsers.notClients(), (companyUser:CompanyUser)=>{
            return !existingUserIds.includes(companyUser.userId)
        })

        _.map(notInWorkspace, (companyUser:CompanyUser)=>{

            const userToAdd = new UserToAdd(companyUser)
            userToAdd.workspaceAccess = this.defaultWorkspaceAccess // we need default access
            userToAdd.projectAccess = this.defaultProjectAccess // we need default access
            models.push(userToAdd)
        })

        this.reset(models)
    }

    resetForProjectInWorkspace(workspace:Workspace, project:ProjectSession, task?: Task):void {

        const models : UserToAdd[] = []

        // 1. find all company users who are not part of this workspace yet
        let existingUserIds : ID[] = project.userIds()
        if (task) existingUserIds = _.union(existingUserIds, task.userIds)

        // 2. find company users who are not customers and not in this workspace yet
        const notInWorkspace : ID[] = _.filter(workspace.project.userIds(), (userId:ID)=>{
            return !existingUserIds.includes(userId)
        })

        _.map(notInWorkspace, (userId:ID)=>{
            const companyUser = gd.session.companyUsers.getByUserId(userId)
            if (companyUser) {
                const userToAdd = new UserToAdd(companyUser)

                const projectUser = gd.session.projectUsers.findByProjectAndUser(workspace.project.id, userId)
                // workspaceAccess is required to know for min project access in INHERITANCE_CHILDREN mode
                userToAdd.workspaceAccess   = projectUser?.access

                // project access can't be lower than workspace access if any
                if (userToAdd.workspaceAccess && this.defaultProjectAccess < userToAdd.workspaceAccess) {
                    userToAdd.projectAccess     = userToAdd.workspaceAccess
                } else {
                    userToAdd.projectAccess     = this.defaultProjectAccess
                }

                models.push(userToAdd)
            }
        })

        this.reset(models)
    }

    resetForProjectWithoutWorkspace(project:ProjectSession, task?: Task):void {
        const models : UserToAdd[] = []

        // 1. find all project users who are not part of this workspace yet
        let existingUserIds : ID[] = project.userIds()
        if (task) existingUserIds = _.union(existingUserIds, task.userIds)

        // 2. find company users who are not customers and not in this workspace yet
        const notInProject : CompanyUser[] = _.filter(gd.session.companyUsers.notClients(), (companyUser:CompanyUser)=>{
            return !existingUserIds.includes(companyUser.userId)
        })

        _.map(notInProject, (companyUser:CompanyUser)=>{
            const userToAdd         = new UserToAdd(companyUser)
            userToAdd.projectAccess = ProjectAccess.STANDARD
            models.push(userToAdd)
        })

        this.reset(models)
    }

    get hasAvailable() {
        return !_.isEmpty(this.available)
    }

    get available():UserToAdd[] {
        let models : UserToAdd[] = this.models

        if (this.manager.searchEnabled) {
            return _.filter(models, (userToAdd: UserToAdd)=>{
                return userToAdd.user.quickSearch(this.manager.searchPrompt)
            })
        }

        if (this.manager.groupByValue === AddUser_ALL) return this.models

        switch (this.manager.groupByFieldId) {
            case gd.fields.companyUser.departmentId.fieldId:
                return this.filterByDepartment()
            case gd.fields.companyUser.skills.fieldId:
                return this.filterBySkills()
            case gd.fields.companyUser.companyRole.fieldId:
                return this.filterByCompanyRole()
        }

        return models
    }

    filterByDepartment():UserToAdd[] {
        return _.filter(this.models, (userToAdd:UserToAdd)=>{
            return userToAdd.companyUser.departmentId === this.manager.groupByValue
        })
    }

    filterByCompanyRole():UserToAdd[] {
        return _.filter(this.models, (userToAdd:UserToAdd)=>{
            return userToAdd.companyUser.companyRole === this.manager.groupByValue
        })
    }

    filterBySkills():UserToAdd[] {
        return _.filter(this.models, (userToAdd:UserToAdd)=>{
            return userToAdd.companyUser.skills?.includes(this.manager.groupByValue)
        })
    }
}